import moment from "moment";

export const createParams = (params) => {
  const {
    searchKey,
    pageNo,
    limit,
    sortOrder,
    sortBy,
    type,
    entityId,
    status,
  } = params;
  let parameters = "?";
  if (type) parameters += `type=${type}&`;
  if (entityId) parameters += `entityId=${entityId}&`;
  if (searchKey) parameters += `searchKey=${searchKey}&`;
  if (pageNo) parameters += `pageNo=${pageNo}&`;
  if (limit) parameters += `limit=${limit}&`;
  if (sortOrder) parameters += `sortOrder=${sortOrder}&`;
  if (sortBy) parameters += `sortBy=${sortBy}&`;
  if (status) parameters += `status=${status}&`;
  return parameters;
};
export const addressFormatter = (data) => {
  if (
    !data ||
    !data.address_components ||
    !data.geometry ||
    !data.formatted_address
  ) {
    return null;
  }

  const getComponent = (type) => {
    const component = data.address_components.find((comp) =>
      comp.types.includes(type)
    );
    return component?.long_name || null;
  };

  return {
    city: getComponent("locality"),
    state: getComponent("administrative_area_level_1"),
    country: getComponent("country"),
    lat:
      typeof data.geometry.location.lat === "function"
        ? data.geometry.location.lat()
        : data.geometry.location.lat,
    lng:
      typeof data.geometry.location.lng === "function"
        ? data.geometry.location.lng()
        : data.geometry.location.lng,
    addressLine: data.formatted_address,
  };
};

/**
 * @function extractCompleteAddress
 * @description Iterate through the address results and extract city, state, country, lat, lng, and addressLine if all present.
 *              Returns the first complete match, or partial if not all are found in any result.
 * @param {Array} addressResults - Array of address results from Google Maps Geocoder or similar API
 * @returns {Object} { city, state, country, lat, lng, addressLine }
 */
/**
 * @function extractLatLngCountry
 * @description Extracts lat, lng, country, and addressLine from the first result that has them.
 *              If none have all, returns best partial match.
 * @param {Array} addressResults - Google Maps Geocoder API results array
 * @returns {Object|null} { lat, lng, country, addressLine }
 */
export const extractLatLngCountry = (addressResults = []) => {
  const getComponent = (components, type) => {
    const comp = components.find((c) => c.types.includes(type));
    return comp?.long_name || null;
  };

  let bestMatch = null;

  for (let data of addressResults) {
    if (
      !data?.address_components ||
      !data?.geometry?.location ||
      !data?.formatted_address
    ) {
      continue;
    }

    const country = getComponent(data.address_components, "country");
    const lat =
      typeof data.geometry.location.lat === "function"
        ? data.geometry.location.lat()
        : data.geometry.location.lat;
    const lng =
      typeof data.geometry.location.lng === "function"
        ? data.geometry.location.lng()
        : data.geometry.location.lng;
    const addressLine = data.formatted_address;

    // Return the first complete set
    if (lat && lng && country) {
      return { lat, lng, country, addressLine };
    }

    // Remember best partial if complete not yet found
    if (!bestMatch && (lat || lng || country)) {
      bestMatch = { lat, lng, country, addressLine };
    }
  }

  return bestMatch || null;
};

export function extractLocationDetails(response) {
  const results = response?.data?.results?.map((result) => {
    const components = result.address_components;
    const geometry = result.geometry?.location;
    const formattedAddress = result.formatted_address;

    const getComponent = (type) => {
      const component = components.find((c) => c.types.includes(type));
      return component?.long_name || null;
    };

    return {
      city: getComponent("locality"),
      state: getComponent("administrative_area_level_1"),
      country: getComponent("country"),
      lat: geometry?.lat ?? null,
      lng: geometry?.lng ?? null,
      addressLine: formattedAddress ?? null,
    };
  });
  return results?.length ? results : [];
}
export const isImage = (url) => {
  if (typeof url === "string") {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".heic",".avif"];
    return imageExtensions.some((ext) => url?.toLowerCase()?.endsWith(ext));
  } else false;
};

export const isVideo = (url) => {
  if (typeof url === "string") {
    const videoExtensions = [".mp4", ".mov", ".webm", ".avi", ".mkv"];
    return videoExtensions.some((ext) => url?.toLowerCase()?.endsWith(ext));
  } else {
    return false;
  }
};

export const getDeleteImageKey = (images, initial) => {
  const imagesToDelete = initial.filter((img) => !images.includes(img));
  const keys = imagesToDelete.map((image) => {
    const temp = image.split("/");
    const key = temp[temp.length - 2] + "/" + temp[temp.length - 1];
    return key;
  });
  return keys;
};

export const dateFormatter = (date) => {
  if (!date) return "N/A";
  return moment(date).format("DD-MM-YYYY");
};

export const getDateAndTimeFormatted = (date) => {
  if (!date) return "N/A";
  const momentDate = moment(date);
  if (!momentDate.isValid()) return "Invalid Date";
  return momentDate.format("DD-MM-YYYY, hh:mm A");
};
